import React, { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import { useAuth } from "../contexts/AuthContext";
import {
  createAdminCategory,
  deleteAdminCategory,
  fetchAdminCategories,
  updateAdminCategory,
} from "../services/adminService";
import "../styles/pages/AdminDashboardPage.css";
import { CategorySummary } from "../types/store";
import { optimizeImageFile } from "../utils/imageUpload";
import { flattenCategoryTree, getCategoryDescendantIds } from "../utils/categoryTree";

const emptyForm = {
  name: "",
  image: "",
  showInNavbar: false,
  parentId: "",
};

type CategoryMode = "category" | "subcategory";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const responseMessage = error.response?.data?.message;
    if (typeof responseMessage === "string" && responseMessage.trim()) {
      return responseMessage;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const isServiceCategory = (category: CategorySummary) =>
  category.slug === "services" || category.name.trim().toLowerCase() === "services";

const AdminCategoriesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [categoryMode, setCategoryMode] = useState<CategoryMode>("category");
  const [formState, setFormState] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null);
  const didInitialLoadRef = useRef(false);
  const loadInFlightRef = useRef(false);
  const lastLoadErrorToastAtRef = useRef(0);

  const handleAdminRequestError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      toast.error("Your admin session has expired. Please log in again.");
      logout();
      navigate("/admin/login", {
        replace: true,
        state: { from: { pathname: location.pathname }, adminOnly: true },
      });
      return;
    }

    if (error instanceof AxiosError && error.response?.status === 403) {
      toast.error(extractErrorMessage(error, "You do not have permission to manage categories."));
      return;
    }

    toast.error(extractErrorMessage(error, fallback));
  }, [location.pathname, logout, navigate]);

  const loadCategories = useCallback(async () => {
    if (loadInFlightRef.current) {
      return;
    }

    loadInFlightRef.current = true;
    try {
      const response = await fetchAdminCategories();
      setCategories(response);
    } catch (error) {
      const now = Date.now();
      if (now - lastLoadErrorToastAtRef.current > 4000) {
        lastLoadErrorToastAtRef.current = now;
        handleAdminRequestError(error, "Unable to load categories right now.");
      }
    } finally {
      loadInFlightRef.current = false;
    }
  }, [handleAdminRequestError]);

  useEffect(() => {
    if (didInitialLoadRef.current) {
      return;
    }

    didInitialLoadRef.current = true;
    void loadCategories();
  }, [loadCategories]);

  const flatCategories = flattenCategoryTree(categories);
  const visibleCategories = flatCategories.filter(({ category, depth }) =>
    !isServiceCategory(category) && (categoryMode === "category" ? depth === 0 : depth > 0),
  );
  const editingCategory = editingId
    ? flatCategories.find(({ category }) => category.id === editingId)?.category ?? null
    : null;
  const excludedParentIds = new Set(
    editingCategory ? getCategoryDescendantIds(editingCategory) : [],
  );
  const isPromoCategory = formState.showInNavbar;

  const selectCategoryMode = (mode: CategoryMode) => {
    setCategoryMode(mode);
    setFormState(emptyForm);
    setEditingId(null);
  };

  const handleCategoryImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadingImage(true);
    try {
      const uploadedImage = await optimizeImageFile(file, {
        maxDimension: 800,
        removeBackground: true,
      });
      setFormState((current) => ({ ...current, image: uploadedImage }));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to prepare the selected category image."));
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  return (
    <section className="shell section page-section">
      <div className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Admin workspace</span>
          <h1>Category management</h1>
        </div>
      </div>
      <AdminWorkspaceNav />

      <div className="admin-category-mode-tabs" role="tablist" aria-label="Category type">
        <button
          type="button"
          role="tab"
          aria-selected={categoryMode === "category"}
          className={categoryMode === "category" ? "is-active" : ""}
          onClick={() => selectCategoryMode("category")}
        >
          Categories
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={categoryMode === "subcategory"}
          className={categoryMode === "subcategory" ? "is-active" : ""}
          onClick={() => selectCategoryMode("subcategory")}
        >
          Subcategories
        </button>
      </div>

      <div className="admin-orders-layout">
        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">
                {categoryMode === "category" ? "Category form" : "Subcategory form"}
              </span>
              <h2>
                {editingId
                  ? `Edit ${categoryMode}`
                  : `Create ${categoryMode}`}
              </h2>
            </div>
          </div>
          <form
            className="form-grid"
            onSubmit={async (event) => {
              event.preventDefault();
              const trimmedName = formState.name.trim();
              if (!trimmedName) {
                toast.error(`Enter a ${categoryMode} name.`);
                return;
              }
              if (categoryMode === "subcategory" && !formState.parentId) {
                toast.error("Select a parent category.");
                return;
              }
              const payload = {
                name: trimmedName,
                slug: slugify(trimmedName),
                description: "",
                icon: "",
                parentId:
                  categoryMode === "subcategory" && formState.parentId
                    ? Number(formState.parentId)
                    : null,
                image: isPromoCategory ? "" : formState.image,
                showInNavbar: categoryMode === "category" && formState.showInNavbar,
              };

              setSavingCategory(true);
              try {
                if (editingId) {
                  await updateAdminCategory(editingId, payload);
                  toast.success(`${categoryMode === "category" ? "Category" : "Subcategory"} updated`);
                } else {
                  await createAdminCategory(payload);
                  toast.success(`${categoryMode === "category" ? "Category" : "Subcategory"} created`);
                }
                setFormState(emptyForm);
                setEditingId(null);
                await loadCategories();
                window.dispatchEvent(new Event("catalog:categories-updated"));
              } catch (error) {
                handleAdminRequestError(error, "Unable to save category right now.");
              } finally {
                setSavingCategory(false);
              }
            }}
          >
            <label>
              {categoryMode === "category" ? "Category name" : "Subcategory name"}
              <input
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder={categoryMode === "category" ? "Appliances" : "Mixer Grinders"}
                required
              />
            </label>
            <label>
              Cover image
              {isPromoCategory ? (
                <span className="admin-form-hint">Not required for promo categories.</span>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => void handleCategoryImageUpload(event)}
                />
              )}
            </label>
            {categoryMode === "subcategory" ? (
              <label>
                Parent category
                <select
                  value={formState.parentId}
                  onChange={(event) =>
                    setFormState((current) => ({ ...current, parentId: event.target.value }))
                  }
                  required
                >
                  <option value="">Select a category</option>
                  {flatCategories
                    .filter(
                      ({ category, depth }) =>
                        depth === 0 &&
                        category.id &&
                        !excludedParentIds.has(category.id) &&
                        !isServiceCategory(category),
                    )
                    .map(({ category }) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              </label>
            ) : null}
            <label className="form-grid__wide">
              Generated slug
              <input value={slugify(formState.name)} readOnly placeholder="appliances" />
            </label>
            {categoryMode === "category" ? (
              <label className="form-grid__wide admin-inline-check">
                <input
                  type="checkbox"
                  checked={formState.showInNavbar}
                  onChange={(event) =>
                    setFormState((current) => ({
                      ...current,
                      showInNavbar: event.target.checked,
                      image: event.target.checked ? "" : current.image,
                      parentId: "",
                    }))
                  }
                />
                <span>Show in navbar</span>
              </label>
            ) : null}
            <p className="form-grid__wide admin-form-hint">
              {categoryMode === "category"
                ? "Creates a top-level category. The optional image appears as its cover image."
                : "Select the top-level category this subcategory belongs to, then add its details."}
            </p>
            {formState.image ? (
              <div className="form-grid__wide admin-image-preview-grid">
                <div className="admin-image-preview-card">
                  <img src={formState.image} alt={formState.name || "Category preview"} />
                  <button
                    className="link-button"
                    type="button"
                    onClick={() => setFormState((current) => ({ ...current, image: "" }))}
                  >
                    Remove image
                  </button>
                </div>
              </div>
            ) : null}
            <div className="admin-form-actions">
              <button className="button" type="submit" disabled={uploadingImage || savingCategory}>
                {savingCategory ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Saving...
                  </span>
                ) : uploadingImage ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Preparing image...
                  </span>
                ) : editingId ? (
                  `Update ${categoryMode}`
                ) : (
                  `Create ${categoryMode}`
                )}
              </button>
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setFormState(emptyForm);
                  setEditingId(null);
                }}
              >
                Clear
              </button>
            </div>
          </form>
        </section>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">
                {categoryMode === "category" ? "Category list" : "Subcategory list"}
              </span>
              <h2>
                {categoryMode === "category" ? "Current categories" : "Current subcategories"}
              </h2>
            </div>
          </div>
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Navbar</th>
                  <th>Products</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleCategories.map(({ category, depth }) => {
                  const canDelete = (category.count ?? 0) === 0 && (category.subcategories?.length ?? 0) === 0;

                  return (
                    <tr key={category.id}>
                      <td>
                        {category.image ? (
                          <img
                            src={category.image}
                            alt={category.name}
                            style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 12 }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{`${"— ".repeat(depth)}${category.name}`}</td>
                      <td>{category.slug}</td>
                      <td>{category.showInNavbar ? "Yes" : "No"}</td>
                      <td>{category.count}</td>
                      <td>
                        <div className="admin-table-actions">
                          <button
                            className="link-button"
                            type="button"
                            onClick={() => {
                              setCategoryMode(depth === 0 ? "category" : "subcategory");
                              setEditingId(category.id || null);
                              setFormState({
                                name: category.name,
                                image: category.showInNavbar ? "" : category.image || "",
                                showInNavbar: Boolean(category.showInNavbar),
                                parentId: category.parentId ? String(category.parentId) : "",
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="link-button admin-danger-button"
                            type="button"
                            disabled={!canDelete || deletingCategoryId === category.id}
                            title={
                              canDelete
                                ? undefined
                                : "Delete linked products and subcategories first"
                            }
                            onClick={async () => {
                              if (!category.id) {
                                return;
                              }
                              if (!canDelete) {
                                toast.error("Delete linked products and subcategories first.");
                                return;
                              }
                              setDeletingCategoryId(category.id);
                              try {
                                await deleteAdminCategory(category.id);
                                toast.success("Category deleted");
                                await loadCategories();
                                window.dispatchEvent(new Event("catalog:categories-updated"));
                              } catch (error) {
                                handleAdminRequestError(error, "Unable to delete category right now.");
                              } finally {
                                setDeletingCategoryId(null);
                              }
                            }}
                          >
                            {deletingCategoryId === category.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
};

export default AdminCategoriesPage;
